<script setup lang="ts">
import { gameWinnerTileKey, type GameWinnerTileProps } from '@game/types'
import { computed, toRef, provide, useFormat, useGame, useDayjs, useClock, trackGameOpen, useGameOpeningGuard, useI18n } from '#imports'

const { asMoney, asRelativeTime } = useFormat()

const props = withDefaults(defineProps<GameWinnerTileProps>(), {
    isMegaPrize: false,
    isBigWin: false,
    variant: 'short',
    horizontal: false,
})

const winner = toRef(props, 'winner')
const isMegaPrize = toRef(props, 'isMegaPrize')
const isBingo = toRef(() => props.variant === 'bingo')
const isBingo90 = toRef(() => winner.value?.slug === 'bingo-90')
const icon = toRef(() => winner.value?.gameIconPath)
const dayjs = useDayjs()
const { clock } = useClock()
const { t } = useI18n()

const { start } = useGame()

const { openingSlug, startOpeningTimeout } = useGameOpeningGuard()

async function startGame() {
    // Don't open a second game while one is already opening — see useGameOpeningGuard.
    if (openingSlug.value !== null) return
    openingSlug.value = winner.value.slug

    trackGameOpen('winner', winner.value, {
        winningAmount: Number(winner.value.amount),
        isBigWin: props.isBigWin,
    })

    try {
        await start({ gameSlug: winner.value.slug })
    }
    finally {
        startOpeningTimeout()
    }
}

const wonAt = computed(() => {
    const timestamp = winner.value.wonAt || winner.value.created
    if (!timestamp) return dayjs(clock.value).tz()

    // Check if timestamp is in the future
    const wonTime = dayjs(timestamp).tz()
    const now = dayjs(clock.value).tz()

    return wonTime.valueOf() > now.valueOf() ? t('core.durations.now') : wonTime
})

const wonTimeAgo = computed(() => {
    if (typeof wonAt.value === 'string') return wonAt.value

    return asRelativeTime(wonAt.value, {
        timeFormat: 'long',
        smallestUnit: 'seconds'
    })
})

const money = computed(() => asMoney(Number(winner.value.amount), { decimals: props.isBigWin || isBingo ? 0 : 2, roundingMode: 'floor' }))

provide(gameWinnerTileKey, {
    winner,
    icon,
    money,
    wonTimeAgo,
    isMegaPrize,
    isBingo,
    startGame,
})
</script>

<template>
    <li
        class="winner-tile"
        :class="{
            'winner-tile--mega-prize': isMegaPrize,
            'winner-tile--big-win': props.isBigWin,
            'winner-tile--bingo': isBingo,
            'winner-tile--bingo-90': isBingo90,
            'winner-tile--horizontal': props.horizontal,
        }"
    >
        <template v-if="isBingo">
            <GameWinnerTileBingo />
        </template>
        <template v-else-if="variant === 'short' || horizontal">
            <GameWinnerTileShort />
        </template>
        <template v-else>
            <GameWinnerTileLong />
        </template>
    </li>
</template>

<style scoped lang="scss">
.winner-tile {
    list-style: none;
    position: relative;
    margin-bottom: 10px;
    padding: 8px;
    border-radius: var(--game-tile-border-radius, $game-tile-border-radius);
    color: var(--game-winner-color, $game-winner-color);
    background: var(--game-winner-bg, $game-winner-bg);
    border: var(--game-winner-border, none);
    font-size: $font-size-xs;
    cursor: pointer;
    backface-visibility: hidden;
    transform-style: preserve-3d;
    will-change: transform;

    &--mega-prize {
        color: var(--game-winner-mega-prize-color, $game-winner-mega-prize-color);
        background: var(--game-winner-mega-prize-bg, $game-winner-mega-prize-bg);
        grid-template-areas: 'image user gamename' 'image amount time';
    }

    &--bingo {
        color: var(--game-winner-bingo-color, $game-winner-bingo-color);
        background: var(--game-winner-bingo-bg, $game-winner-bingo-bg);
        border: var(--game-winner-bingo-border, $game-winner-bingo-border);

        @media (min-width: $screen-md) {
            min-width: 137px;
            margin: 0 8px;
            padding: 5px;

            &:first-child {
                margin-left: 0;
            }
        }

        &:nth-child(1n + 5),
        &:nth-child(1n + 6) {
            display: none;
        }

        &:nth-child(1n + 5) {
            @media (min-width: $screen-xl) {
                display: block;
            }
        }

        &:nth-child(1n + 4) {
            // Tablet portrait only
            @media (min-width: $screen-md) and (max-width: #{$screen-lg - 1px}) {
                display: none;
            }

            @media (min-width: $screen-xl) {
                display: block;
            }
        }

        &.winner-tile--mega-prize {
            min-width: 224px;
            max-width: 60vw;
            background: var(--game-winner-bingo-mega-prize-bg, $game-winner-bingo-mega-prize-bg);
            box-shadow: 0 6px 16px var(--game-winner-bingo-mega-prize-shadow-color, $game-winner-bingo-mega-prize-bg);
            border: var(--game-winner-bingo-mega-prize-border, $game-winner-bingo-mega-prize-border);
            transition-delay: 0.38s !important;

            @media (min-width: $screen-md) {
                width: 245px;
            }

            @media (min-width: $screen-lg) {
                height: 100%;
            }

            @media (min-width: $screen-xl) {
                transition-delay: 0.56s !important;
            }
        }
    }

    &--big-win {
        background: var(--game-winner-big-win-bg, $game-winner-big-win-bg);
        color: var(--game-winner-big-win-color, $game-winner-big-win-color);
        border: var(--game-winner-big-win-border, var(--game-winner-border, none));
    }

    &--horizontal {
        margin-right: 10px;
        min-width: 160px;

        @media (min-width: $screen-md) {
            min-width: 180px;
        }
    }
}
</style>
